const semver = require('semver');

// A version keeps the status its review ended on. Shared so every bundle and
// the server name these the same way, and so they are not mistaken for the
// partner directory listing's own `rejected` / `approved`, which are a
// different enum on a different model.
const REVIEW_STATUS = Object.freeze({
  IN_REVIEW: 'in_review',
  NEEDS_CHANGES: 'needs_changes',
  REJECTED: 'rejected',
  UNAPPROVED: 'unapproved',
  APPROVED: 'approved',
});

/**
 * Check if version1 is greater than version2.
 * @param {string} version1
 * @param {string} version2
 * @returns {boolean}
 */
function isVersionGreater(version1, version2) {
  if (typeof version1 !== 'string' || typeof version2 !== 'string') {
    return false;
  }

  return semver.compare(version1, version2) > 0;
}

/**
 * Get new publishable version record if available.
 * Reads `publishable_versions` (released, not yet published) and
 * `published_props.version`.
 * @param {object} app
 * @returns {object|undefined}
 */
function getNewPublishableVersionRecord(app) {
  const { publishable_versions, published_props } = app;
  const publishedVersion = published_props?.version;
  const publishableVersionRecord = publishable_versions?.results?.[0];
  const publishableVersion = publishableVersionRecord?.version;

  if (!publishableVersion) {
    return;
  }

  if (
    !publishedVersion ||
    isVersionGreater(publishableVersion, publishedVersion)
  ) {
    return publishableVersionRecord;
  }
}

/**
 * Get new publishable version if available.
 * @param {object} app
 * @returns {string|undefined}
 */
function getNewPublishableVersion(app) {
  return getNewPublishableVersionRecord(app)?.version;
}

/**
 * The app version whose review is open — the review both sides may still write
 * comments on. Staging a release opens it, and a request for changes leaves it
 * open so the partner can answer the reviewer. It closes when the release is
 * published, when the app is rejected outright, or when the partner releases a
 * newer version to be reviewed in its place.
 * @param {object} app
 * @returns {string|undefined}
 */
function getOpenReviewVersion(app) {
  const stagedVersion = app.staged_props?.version;

  if (stagedVersion) {
    return stagedVersion;
  }

  const versionRecord = getNewPublishableVersionRecord(app);

  return versionRecord?.review_status === REVIEW_STATUS.NEEDS_CHANGES
    ? versionRecord.version
    : undefined;
}

/**
 * Whether the app was rejected outright. A rejection is terminal, so it is read
 * across every version rather than only the one waiting to be published.
 * @param {object} app
 * @returns {boolean}
 */
function isAppReviewRejected(app) {
  const versions = app.versions?.results || [];

  return versions.some(
    ({ review_status: reviewStatus }) =>
      reviewStatus === REVIEW_STATUS.REJECTED,
  );
}

module.exports = {
  REVIEW_STATUS,
  isVersionGreater,
  getNewPublishableVersionRecord,
  getNewPublishableVersion,
  getOpenReviewVersion,
  isAppReviewRejected,
};
